import { trpc } from './trpc';
import { inferRouterOutputs, inferRouterInputs } from '@trpc/server';
import { AppRouter } from '@tagadapay/trpc-types';

type OutputRouter = inferRouterOutputs<AppRouter>
type InputRouter = inferRouterInputs<AppRouter>

// Output types
export type ProcessorListOutput = OutputRouter['processors']['list']
export type Processor = ProcessorListOutput[number]
export type ProcessorDetails = OutputRouter['processors']['getById']
export type CreateProcessorOutput = OutputRouter['processors']['create']
export type UpdateProcessorOutput = OutputRouter['processors']['update']
export type DeleteProcessorsOutput = OutputRouter['processors']['delete']
export type TagadapayAccountsOutput = OutputRouter['processors']['listTagadapayAccounts']
export type TagadapayAccount = TagadapayAccountsOutput[number]

// Input types
export type GetProcessorByIdInput = InputRouter['processors']['getById']
export type CreateProcessorInput = InputRouter['processors']['create']
export type UpdateProcessorInput = InputRouter['processors']['update']
export type DeleteProcessorsInput = InputRouter['processors']['delete']

// Query hooks
export const useProcessorsList = () => {
    return trpc.processors.list.useQuery();
}

export const useProcessorById = (processorId: string) => {
    return trpc.processors.getById.useQuery(
        { processorId },
        { enabled: !!processorId }
    );
}

/** TagadaPay accounts (TPAs) available to the current organisation. */
export const useTagadapayAccounts = () => {
    return trpc.processors.listTagadapayAccounts.useQuery(undefined, {
        staleTime: 15_000,
        refetchOnWindowFocus: true,
    });
}
/**
 * Which APM methods each processor type can actually be charged with, read
 * server-side from the charge path's own map (#TAG-2506). Derived from code,
 * identical for every tenant and every session — so it is fetched once and
 * never refetched.
 *
 * Given a `processorId`, the answer also names what that processor's own
 * Stripe account has not activated (#TAG-3194). That part is read live from
 * Stripe and changes when the merchant activates a method there, so it is
 * refetched when they come back to the tab.
 */
export const useApmCapabilities = (processorId?: string) => {
    return trpc.processors.apmCapabilities.useQuery(
        processorId ? { processorId } : undefined,
        processorId
            ? { staleTime: 60_000, refetchOnWindowFocus: true }
            : { staleTime: Infinity, refetchOnWindowFocus: false },
    );
}

// Mutation hooks
export const useCreateProcessor = () => {
    const utils = trpc.useUtils();

    return trpc.processors.create.useMutation({
        onSuccess: () => {
            utils.processors.list.invalidate();
        },
    });
}

export const useUpdateProcessor = () => {
    const utils = trpc.useUtils();

    return trpc.processors.update.useMutation({
        onSuccess: () => {
            utils.processors.list.invalidate();
            utils.processors.getById.invalidate();
        },
    });
}

export const useDeleteProcessors = () => {
    const utils = trpc.useUtils();

    return trpc.processors.delete.useMutation({
        onSuccess: () => {
            utils.processors.list.invalidate();
        },
    });
}
